import { DatePipe } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, of, shareReplay } from 'rxjs';
import { EventPublishingSettingsModel } from 'src/app/models/event-publishing-settings/event-publishing-settings.model';
import { TrainingLocationTypeModel } from 'src/app/models/training/training-location-type.model';
import { TrainingSummaryModel } from 'src/app/models/training/training-summary.model';
import { LoadingService } from 'src/app/services/loading.service';
import { TrainingPublicOverviewService } from 'src/app/services/training-public-overview.service';

@Component({
  selector: 'app-public-training-overview',
  standalone: false,
  templateUrl: './public-training-overview.component.html',
  styleUrl: './public-training-overview.component.scss',
})
export class PublicTrainingOverviewComponent {
  settings: EventPublishingSettingsModel | null = null;
  logoUrl: string | null = null;

  trainings: TrainingSummaryModel[] = [];
  filteredTrainings: TrainingSummaryModel[] = [];
  groupedTrainings: any[] = [];

  filters = {
    searchString: '',
    trainingCategories: [] as string[],
    eventTypes: [] as number[]
  };

  trainingCategories: any[] = [];
  eventTypes: any[] = [];

  isInvalidToken: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private loadingService: LoadingService,
    private trainingPublicOverviewService: TrainingPublicOverviewService
  ) {}

  ngOnInit(): void {
    const token = this.route.snapshot.paramMap.get('accessToken')!;

    const data$ = this.loadingService.load(
      forkJoin({
        settings: this.trainingPublicOverviewService.getSettings(token),
        logo: this.trainingPublicOverviewService.getLogo(token),
        trainings: this.trainingPublicOverviewService.getTrainings(token)
      })
    )
    .pipe(
      catchError(() => {
        this.isInvalidToken = true;
        return of(null);
      }),
      shareReplay(1)
    );

    data$.subscribe(result => {
      if (!result) return;
      const { settings, logo, trainings } = result;

      this.settings = settings;
      this.logoUrl = logo;

      this.trainings = trainings;
      this.seedFilters(trainings);

      this.applyFilters();
    });
  }

  private seedFilters(trainings: TrainingSummaryModel[]) {
    this.eventTypes = [
      {
        id: 0,
        name: this.translateService.instant('COMMON.FIELDS.EVENT-TYPES.IN-PERSON'),
      },
      {
        id: 1,
        name: this.translateService.instant('COMMON.FIELDS.EVENT-TYPES.WEBINAR'),
      },
    ];
    
    const categoryMap = new Map<number, string>();

    trainings.forEach(t => {
      (t.trainingCategories || []).forEach((c: any) => {
        categoryMap.set(c.id, c.name);
      });
    });

    this.trainingCategories = Array.from(categoryMap.entries())
      .map(([id, name]) => ({
        id,
        name
      }));
  }

  onTrainingCategorySelected(e: any) {
    if (e.node.selected) {
      this.filters.trainingCategories = [
        ...this.filters.trainingCategories!,
        e.node.key,
      ];
    } else {
      this.filters.trainingCategories = this.filters.trainingCategories!.filter(
        (id) => id !== e.node.key
      );
    }

    this.applyFilters();
  }

  onEventTypeSelected(e: any) {
    if (e.node.selected) {
      this.filters.eventTypes = [...this.filters.eventTypes, e.node.key];
    } else {
      this.filters.eventTypes = this.filters.eventTypes.filter(
        (id) => id !== e.node.key
      );
    }

    this.applyFilters();
  }

  onSearchInput(event: any) {
    this.filters.searchString = event.value;
    this.applyFilters();
  }


  applyFilters() {
    let data = [...this.trainings];

    // SEARCH
    if (this.filters.searchString?.trim()) {
      const search = this.filters.searchString.toLowerCase();

      data = data.filter(t =>
        t.name?.toLowerCase().includes(search) ||
        t.description?.toLowerCase().includes(search)
      );
    }

    // CATEGORY FILTER
    if (this.filters.trainingCategories.length) {
      data = data.filter(t =>
        t.trainingCategories?.some((c: any) =>
          this.filters.trainingCategories.includes(c.id)
        )
      );
    }

    // EVENT TYPE FILTER
    if (this.filters.eventTypes.length) {
      data = data.filter(t => {
        const isWebinar = t.location && t.location.type !== TrainingLocationTypeModel.Address;
        const isInPerson = t.location?.type === TrainingLocationTypeModel.Address;

        const matchInPerson =
          this.filters.eventTypes.includes(0) && isInPerson;

        const matchWebinar =
          this.filters.eventTypes.includes(1) && isWebinar;

        return matchInPerson || matchWebinar;
      });
    }

    this.filteredTrainings = data;
    this.groupedTrainings = this.groupByMonth(data);
  }

  private groupByMonth(trainings: TrainingSummaryModel[]) {
    const groups: Record<string, any[]> = {};

    for (const t of trainings) {
      const date = new Date(t.dates[0].start);

      const key =
        new DatePipe(this.translateService.currentLang).transform(
          new Date(date),
          'MMMM yyyy'
        ) || '';

      if (!groups[key]) {
        groups[key] = [];
      }

      groups[key].push(t);
    }

    return Object.entries(groups)
      .map(([month, items]) => ({
        month,
        items
      }))
      .sort((a, b) =>
        new Date(b.items[0].startDate).getTime() -
        new Date(a.items[0].startDate).getTime()
      );
  }
}
